'use client';

import { useEffect, useRef } from 'react';
import appLogger from '@/utils/logger';
import { hasActiveLlmStream } from '@/utils/activeLlmStreamGuard';
import { isSalesforceEmbedFrame } from '@/utils/salesforceEmbed';

const BUILD_ID_STORAGE_KEY = 'mb_build_id';
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
// How long to wait before re-checking a reload that was deferred because a chat send was
// mid-stream -- short enough that the tab still catches up to a new deploy promptly once
// idle, without needing to wait for the next full 5-minute cycle or a tab-focus event.
const DEFERRED_RECHECK_MS = 15 * 1000;

/**
 * BuildVersionChecker - Detects deployment mismatches and triggers page reloads
 *
 * Prevents "Failed to find Server Action" errors by detecting when the server
 * has been redeployed with a new build ID while the client still has cached
 * JavaScript with old Server Action IDs.
 *
 * Detection strategies:
 * 1. Immediate check on mount
 * 2. Periodic polling every 5 minutes
 * 3. Immediate check when tab regains focus (visibilitychange)
 *
 * The visibility check is critical — the most common failure path is a user
 * returning to a stale tab after a deployment and interacting before the
 * next polling cycle.
 *
 * Never runs inside a Salesforce embed (`isSalesforceEmbedFrame`): a reload
 * there drops the in-memory Canvas sign-in and only Salesforce can re-sign
 * the frame, so every deploy used to break an open panel.
 */
export default function BuildVersionChecker() {
  const initializedRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || isSalesforceEmbedFrame()) {
      return;
    }

    // Prevent double initialization in React Strict Mode
    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;

    const checkBuildVersion = async () => {
      try {
        const response = await fetch('/api/internal/build-version', {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
          },
        });

        if (!response.ok) {
          appLogger.warn('BuildVersionChecker: Failed to fetch build version', {
            status: response.status,
          });
          return;
        }

        const { buildId } = await response.json();

        if (!buildId || buildId === 'unknown') {
          appLogger.warn('BuildVersionChecker: Received unknown build ID');
          return;
        }

        const storedBuildId = localStorage.getItem(BUILD_ID_STORAGE_KEY);

        if (!storedBuildId) {
          // First visit or cleared storage - store current build ID
          localStorage.setItem(BUILD_ID_STORAGE_KEY, buildId);
          appLogger.info('BuildVersionChecker: Initial build ID stored', { buildId });
          return;
        }

        if (storedBuildId !== buildId) {
          // Don't reload out from under an in-progress chat send (confirmed live,
          // 2026-08-07: a deploy landing mid-send silently discarded the user's message --
          // the request was aborted by the reload before the server ever received it, with
          // no error shown). Leave localStorage untouched so the mismatch is still detected
          // and retried once the stream finishes, rather than reloading immediately.
          if (hasActiveLlmStream()) {
            appLogger.info('BuildVersionChecker: deferring reload, a chat send is in progress', {
              storedBuildId,
              serverBuildId: buildId,
            });
            setTimeout(checkBuildVersion, DEFERRED_RECHECK_MS);
            return;
          }

          // Build ID mismatch - new deployment detected
          appLogger.warn('BuildVersionChecker: Build version mismatch detected', {
            storedBuildId,
            serverBuildId: buildId,
            action: 'reloading',
          });

          // Update stored build ID before reload to prevent reload loops
          localStorage.setItem(BUILD_ID_STORAGE_KEY, buildId);

          // Brief delay to ensure log is sent before reload
          setTimeout(() => {
            window.location.reload();
          }, 100);
        }
      } catch (error) {
        // Network errors are expected during deployments - don't spam logs
        appLogger.debug('BuildVersionChecker: Version check failed', {
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    };

    // Check immediately on mount
    checkBuildVersion();

    // Set up periodic checks
    intervalRef.current = setInterval(checkBuildVersion, CHECK_INTERVAL_MS);

    // Check immediately when user returns to the tab — this catches the most
    // common scenario: user has tab open, deployment happens while tab is
    // backgrounded, user switches back and would otherwise interact with stale JS.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkBuildVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // This component doesn't render anything
  return null;
}
